import { createRootRouteWithContext, createRoute } from "@tanstack/react-router";
import type { RouterContext } from "@empite/app-kit";
import { Layout } from "./screens/layout";
import { Browse } from "./screens/browse";
import type { BrowseSearch } from "./screens/browse";
import { CreationDetail } from "./screens/creation-detail";
import { MyCreations } from "./screens/my-creations";
import { UploadWizard } from "./screens/upload-wizard";
import type { UploadSearch } from "./screens/upload-wizard";

/**
 * The route table.
 *
 * Every screen that reads the URL is handed its search parameters as a PROP,
 * parsed by the route's own validateSearch — so a hostile link cannot reach a
 * component as an unexpected type. Same rule as the customer portal's, and
 * for the same reason.
 */
function text(value: unknown): string | undefined {
  return typeof value === "string" && value !== "" ? value : undefined;
}

const rootRoute = createRootRouteWithContext<RouterContext>()({ component: Layout });

const browseRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  validateSearch: (search: Record<string, unknown>): BrowseSearch => ({ q: text(search.q) }),
  component: () => <Browse search={browseRoute.useSearch()} />,
});

const creationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/creations/$creationID",
  component: () => <CreationDetail creationID={creationRoute.useParams().creationID} />,
});

const uploadRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/upload",
  validateSearch: (search: Record<string, unknown>): UploadSearch => ({
    creation: text(search.creation),
  }),
  component: () => <UploadWizard search={uploadRoute.useSearch()} />,
});

const mineRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/mine",
  component: MyCreations,
});

export const routeTree = rootRoute.addChildren([browseRoute, creationRoute, uploadRoute, mineRoute]);
